import { Theme } from '../../config/theme';
import { AssetType, CmsProperty } from '../../api/models/Shared.types';
import { formatCardSchedule, formatOldCardSchedule, formatTime } from '../utility/DateFormatter';
import { ScheduleExtractor } from './ScheduleExtractor';
import { fullUrlWithRecipe, Recipes } from '../utility/ImageRecipeUtil';
import { Log, Utils } from '@lightningjs/sdk';
import { CmsCardIcon } from '../../components/cms/CmsCardIcon';
import { CmsFolderIcon } from '../../components/cms/CmsFolderIcon';
import { EpgUtil } from '../utility/EpgUtil';
import { GuideTranslationKeys, SettingsKeys } from '../../api/models/TranslationKeys';
import { GlobalSettingsKeys } from '../../api/models/GlobalSettingsKeys';
import { CardDataSource } from './CardDataSource';
import { IRibbon2Detail, IRibbon2Schedule } from '../../api/models/AndromeTV.types';
import { Recording } from '../../api/user/RecordingApi.types';
import { FavoriteID } from '../../api/user/FavoriteApi.types';
import { FavoriteRequestType } from './FavoriteRibbonRequest';
import { Backend } from '../../api/Backend';
import { AccessibilityIcons, AccessibilityTypes } from '../AccessibilityTypes';

export interface CardInfoIcon {
  type: unknown;
  icon?: string;
  amount?: number;
}

export class CardInfo {
  public dataSource: CardDataSource;
  public topLine: string | undefined;
  public topLineColor: number;
  public topLineBold: boolean;
  public bottomLine: string | undefined;
  public bottomLineColor: number;
  public bottomLineBold: boolean;
  public recordingFolderAmount: number;
  public extractor: ScheduleExtractor | undefined;
  public icons: CardInfoIcon[];
  private _iconPromise: Promise<CardInfoIcon[]> | undefined;
  public bookmarkPosition: number | undefined;
  private _bestSchedule: boolean | undefined;
  private _showSchedule: boolean | undefined;
  private _showProgress: boolean | undefined;
  private _hideInfo: boolean | undefined;
  private _showEpisode: boolean | undefined;
  private _adultRequired: boolean | undefined;
  public channelLogo: string | null | undefined;
  public imageUrl: string | null | undefined;
  public scheduleLine: string;

  // Old design for EPG cards
  public useOldEpgCards: boolean | undefined;
  private epgGuideItem: boolean;

  constructor(dataSource: CardDataSource, epgGuideItem = false) {
    this.dataSource = dataSource;
    this.topLine = '';
    this.topLineColor = Theme.Colors.Ribbon.Card.Text.Normal;
    this.topLineBold = false;
    this.bottomLine = '';
    this.bottomLineColor = Theme.Colors.Ribbon.Card.Text.Normal;
    this.bottomLineBold = false;
    this.recordingFolderAmount = 0;
    this.extractor = undefined;
    this._updateFlags();
    this.icons = [];
    this._iconPromise = undefined;
    this.scheduleLine = '';
    this.useOldEpgCards = this.dataSource.backend.globalSettings.boolValue(SettingsKeys.useOldEpgCards) || false;
    this.epgGuideItem = epgGuideItem;
  }

  /**
   * calculate the info field data for a card
   * @returns {Promise<CardInfo>}
   */
  cardInfo(): Promise<CardInfo> {
    // console.log('Detecting cardInfo', this);
    switch (this.dataSource.asset.asset.type) {
      case AssetType.CHANNEL:
        return this.updateForChannel();
      case AssetType.PROGRAM:
        return this.updateForProgram();
      case AssetType.SCHEDULE:
        return this.updateForProgram();
      case AssetType.SERIES:
        return this.updateForSeries();
      case AssetType.VOD:
        return this.updateForVod();
      case AssetType.BUNDLE:
        return this.updateForBundle();
      case AssetType.ARTICLE:
        return this.updateForArticle();
      case AssetType.RECORDING:
        return this.updateForProgram();
      case AssetType.PAGE:
        return this.updateForPage();
      default:
        Log.warn('Unsupported asset type', this.dataSource.asset.asset.type);
        return Promise.resolve(this);
    }
  }

  updateForArticle(): Promise<CardInfo> {
    // console.log(this);
    return new Promise(resolve => {
      const item = this.dataSource.asset;
      this._detectImage(item.asset);
      this.topLine = item.asset.title;
      resolve(this);
    });
  }

  updateForPage(): Promise<CardInfo> {
    // console.log(this);
    return new Promise(resolve => {
      const item = this.dataSource.asset;
      this._detectImage(item.asset);
      this.topLine = this.dataSource.asset.asset.title;
      resolve(this);
    });
  }

  /**
   * Update info fields for a channel card
   */
  updateForChannel(): Promise<CardInfo> {
    return new Promise(resolve => {
      this.extractor = new ScheduleExtractor(this.dataSource);
      // console.log('Channel extracted', extractor);
      this._configureGeneric(this.extractor.schedule, this.extractor.detail);
      this._detectImage(this.extractor.detail);
      if (this._bestSchedule || this._showSchedule) {
        this._detectChannelLogo(this.extractor);
      }

      this._scheduleBookmark(this.extractor.schedule, this.extractor.detail).then(bookmark => {
        this.bookmarkPosition = bookmark;
        resolve(this);
      });
    });
  }

  updateForProgram(): Promise<CardInfo> {
    return new Promise(resolve => {
      this.extractor = new ScheduleExtractor(this.dataSource);
      // console.log('Program extracted', extractor);

      this._detectChannelLogo(this.extractor);
      this._detectImage(this.extractor.detail);
      this._configureGeneric(this.extractor.schedule, this.extractor.detail);
      this._setRecordingFolderAmount();
      this._scheduleBookmark(this.extractor.schedule, this.extractor.detail).then(bookmark => {
        this.bookmarkPosition = bookmark;
        resolve(this);
      });
    });
  }

  updateForSeries(): Promise<CardInfo> {
    return new Promise(resolve => {
      const szi = this.dataSource.sizeInfo;
      this.extractor = new ScheduleExtractor(this.dataSource);
      // console.log('Series extracted', extractor);
      this._detectImage(this.extractor.detail);
      this._detectChannelLogo(this.extractor);
      this._configureGeneric(this.extractor.schedule, this.extractor.detail);
      if (szi.showProgress) {
        this._scheduleBookmark(this.extractor.schedule, this.extractor.detail).then(bookmark => {
          this.bookmarkPosition = bookmark;
          resolve(this);
        });
      } else {
        resolve(this);
      }
    });
  }

  updateForVod(): Promise<CardInfo> {
    return new Promise(resolve => {
      const item = this.dataSource.asset;
      if (this._hideInfo && this._showEpisode) {
        this.topLine = this._seasonEpisodeString(item.asset);
      } else {
        this.topLine = this.titleForItem(item.asset);
      }
      this._detectImage(item.asset);
      this._cachedBookmark(item.asset.type as AssetType, item.asset.id).then(bookmark => {
        this.bookmarkPosition = bookmark;
        resolve(this);
      });
    });
  }

  updateForBundle(): Promise<CardInfo> {
    return new Promise(resolve => {
      const item = this.dataSource.asset;
      this._detectImage(item.asset);

      this.topLine = this.titleForItem(item.asset);
      resolve(this);
    });
  }

  titleForItem(item: IRibbon2Detail) {
    return item.adult
      ? this.dataSource.backend.translations.forKey(GuideTranslationKeys.guide_adult_title)
      : item.title;
  }

  _configureGeneric(schedule: IRibbon2Schedule | undefined, detail: IRibbon2Detail | undefined) {
    if (this.epgGuideItem) {
      this._configureMiniEpg(schedule, detail);
    } else if (this._bestSchedule && this._showSchedule) {
      this._configureBestAndShowSchedule(schedule, detail);
    } else if (this._bestSchedule) {
      this._configureBestSchedule(schedule, detail);
    } else if (this._showSchedule) {
      this._configureShowSchedule(schedule, detail);
    } else {
      this._configureRegular(detail);
    }
  }

  _configureMiniEpg(schedule: IRibbon2Schedule | undefined, detail: IRibbon2Detail | undefined) {
    this._setScheduleTheme();
    this.topLine = this._detailTitle(detail);
    if (this.dataSource.backend.globalSettings.boolValue(GlobalSettingsKeys.epgShowMiniGuideSeasonAndEpisode)) {
      this.bottomLine =
        this._miniEpgTimeString(this.dataSource.backend, schedule) + '  ' + this._seasonEpisodeStringShort(detail);
    } else {
      this.bottomLine = this._miniEpgTimeString(this.dataSource.backend, schedule);
    }
  }

  _configureBestAndShowSchedule(schedule: IRibbon2Schedule | undefined, detail: IRibbon2Detail | undefined) {
    this._setScheduleTheme();
    if (this._hideInfo) {
      if (this._showEpisode) {
        this.topLine = this._seasonEpisodeString(detail);
      }
    } else if (this.useOldEpgCards) {
      this.configureOldEpgCard(schedule, detail);
    } else {
      if (this._showEpisode) {
        this.topLine = this._episodeStringFull(detail);
      } else {
        this.topLine = this._detailTitle(detail);
      }
      if (this.topLine === null || this.topLine === undefined) this.topLine = this._detailTitle(detail);
      this.bottomLine = this._scheduleTimeString(schedule);
    }
  }

  _configureBestSchedule(schedule: IRibbon2Schedule | undefined, detail: IRibbon2Detail | undefined) {
    this._setScheduleTheme();
    if (this._hideInfo) {
      if (this._showEpisode) {
        this.topLine = this._seasonEpisodeString(detail);
      }
    } else if (this.useOldEpgCards) {
      this.configureOldEpgCard(undefined, detail);
    } else {
      this.topLine = this._detailTitle(detail);
      this.bottomLine = this._scheduleTimeString(schedule);
    }
  }

  _configureShowSchedule(schedule: IRibbon2Schedule | undefined, detail: IRibbon2Detail | undefined) {
    this._setScheduleTheme();
    if (this._hideInfo) {
      if (this._showEpisode) {
        this.topLine = this._seasonEpisodeString(detail);
      }
    } else if (this.useOldEpgCards) {
      this.configureOldEpgCard(schedule, detail);
    } else {
      this.topLine = this._detailTitle(detail);
      this.bottomLine = this._scheduleTimeString(schedule);
    }
  }

  _configureRegular(detail: IRibbon2Detail | undefined) {
    if (this._hideInfo) {
      if (this._showEpisode) {
        this.topLine = this._seasonEpisodeString(detail);
      }
    } else if (this._showEpisode) {
      this.topLine = this._seasonEpisodeTitle(detail);
    } else {
      this.topLine = this._detailTitle(detail);
      if (this.useOldEpgCards && detail?.subType === 'CHANNEL_TOGGLE') {
        this.topLineColor = Theme.Colors.Ribbon.Card.Text.Highlight;
        this.topLineBold = true;
      }
    }
  }

  private configureOldEpgCard(schedule: IRibbon2Schedule | undefined, detail: IRibbon2Detail | undefined) {
    if (schedule) {
      this.scheduleLine = this._scheduleTimeString(schedule, true);
    }
    this.topLine = this.channelNumberName(this.extractor, detail);
    if (detail?.subType !== 'CHANNEL_LOGO') {
      if (this._showEpisode) {
        this.bottomLine = this._episodeStringFull(detail) || this._detailTitle(detail);
      } else {
        this.bottomLine = this._detailTitle(detail);
      }
      this.bottomLine = this.bottomLine || this._detailTitle(detail) || '';
    }
  }

  _seasonEpisodeString(detail: IRibbon2Detail | undefined): string | undefined {
    if (!detail) return undefined;

    if (detail.season && detail.season > 0 && detail.episode && detail.episode > 0) {
      const val = this.dataSource.backend.translations.forKey('ribbon_season_episode');
      return val.replace(/\$1/g, detail.season.toString()).replace(/\$2/g, detail.episode.toString());
    } else if (detail.episode) {
      const val = this.dataSource.backend.translations.forKey('ribbon_episode');
      return val.replace(/\$1/g, detail.episode.toString());
    }
    return undefined;
  }

  _seasonEpisodeStringShort(detail: IRibbon2Detail | undefined): string {
    if (!detail) return '';
    let seasonEpStr = '';

    if (detail.season && detail.season > 0) {
      const val = this.dataSource.backend.translations.forKey('detail_season_short');
      seasonEpStr = `${val}${detail.season.toString()} `;
    }
    if (detail.episode && detail.episode > 0) {
      const val = this.dataSource.backend.translations.forKey('detail_episode_short');
      seasonEpStr += `${val}${detail.episode.toString()}`;
    }
    return seasonEpStr;
  }

  _episodeStringFull(detail: IRibbon2Detail | undefined): string | undefined {
    if (!detail) return undefined;

    if (detail.adult) {
      return this.dataSource.backend.translations.forKey(GuideTranslationKeys.guide_adult_title);
    }

    if (detail.episode && detail.episodeTitle) {
      return (
        this.dataSource.backend.translations.forKey('detail_episode_short') +
        ` ${detail.episode} • ` +
        detail.episodeTitle
      );
    } else if (detail.episode) {
      return this.dataSource.backend.translations.forKey('detail_episode') + ` ${detail.episode}`;
    } else if (detail.episodeTitle) {
      return detail.episodeTitle;
    }
    return undefined;
  }

  _seasonEpisodeTitle(detail: IRibbon2Detail | undefined): string | undefined {
    if (!detail) return undefined;

    if (detail.adult) {
      return this.dataSource.backend.translations.forKey(GuideTranslationKeys.guide_adult_title);
    }
    const seasonEpisode = this._seasonEpisodeString(detail);
    const title = detail.title;
    if (title && seasonEpisode) {
      return `${title} ${seasonEpisode}`;
    } else if (title) {
      return title;
    } else {
      return seasonEpisode;
    }
  }

  _detailTitle(detail: IRibbon2Detail | undefined): string | undefined {
    if (!detail) return undefined;

    if (detail.adult) {
      return this.dataSource.backend.translations.forKey(GuideTranslationKeys.guide_adult_title);
    }
    return detail.title;
  }

  _scheduleBookmark(
    schedule: IRibbon2Schedule | undefined,
    detail: IRibbon2Detail | undefined
  ): Promise<number | undefined> {
    const shouldShowProgress = this._showProgress || this._showSchedule || this._bestSchedule;
    if (!shouldShowProgress || (!schedule && !detail)) {
      return Promise.resolve(undefined);
    }
    if (schedule) {
      const end = schedule.published.end;
      const start = schedule.published.start;
      const now = new Date().getTime() / 1000;
      if (+start <= now && now <= +end) {
        return new Promise(resolve => {
          if (end > start) {
            resolve((now - +start) / (+end - +start));
          } else {
            resolve(undefined);
          }
        });
      }
    }
    if (detail) {
      return this._cachedBookmark(detail.type as AssetType, detail.id);
    }
    return Promise.resolve(undefined);
  }

  _cachedBookmark(type: AssetType, id: string): Promise<number | undefined> {
    const backend = this.dataSource.backend;
    return backend.cachedBookmarkApi
      .bookmark(backend, { type: type, id: id }, false)
      .then(data => {
        if (data && data.size > 0) {
          // // console.log(data, this);
          return data.position / data.size;
        }
        return undefined;
      })
      .catch(err => {
        Log.error('[CardInfo] Bookmark Cache Error', err);
        return undefined;
      });
  }

  _scheduleTimeString(schedule: IRibbon2Schedule | undefined, oldFormat = false): string {
    if (schedule && schedule.published) {
      if (oldFormat) {
        const start = new Date(+schedule.published.start * 1000);
        const end = new Date(+schedule.published.end * 1000);
        return formatOldCardSchedule(this.dataSource.backend, start, end);
      } else {
        const start = new Date(+schedule.published.start * 1000);
        return formatCardSchedule(this.dataSource.backend, start);
      }
    }
    return '';
  }

  _miniEpgTimeString(backend: Backend, schedule: IRibbon2Schedule | undefined): string {
    let time = '';
    if (schedule && schedule.published) {
      const start = new Date(+schedule.published.start * 1000);
      const end = new Date(+schedule.published.end * 1000);
      time = formatTime(start, end);
    }
    return time;
  }

  _detectChannelLogo(extractor: ScheduleExtractor) {
    // Hide channel logo if old EPG card design is used
    if (extractor.channel && !this.useOldEpgCards && !this.epgGuideItem) {
      this.channelLogo = fullUrlWithRecipe(
        this.dataSource.backend,
        extractor.channel.image.defaultLogo,
        Recipes.SmallChannelLogo
      );
    }
  }

  private channelNumberName(extractor: ScheduleExtractor | undefined, detail: IRibbon2Detail | undefined): string {
    const channel = extractor ? extractor.channel : undefined;
    if (channel) {
      const catId = this.dataSource.backend.deviceProfile?.profile.catalog;
      const catalog = channel.catalogs ? channel.catalogs.find(cat => cat.catalogId === catId) : undefined;
      let channelNumber = '';
      if (catalog) {
        channelNumber = `${catalog.channelNumber} • `;
      }
      return `${channelNumber}${channel.text.title}`;
    }
    if (detail && detail.subType === 'CHANNEL_LOGO') {
      return detail.title || '';
    }
    return '';
  }

  _detectImage(detail: IRibbon2Detail | undefined) {
    if (detail?.adult) {
      this.imageUrl = Utils.asset('/images/adult.png');
      return;
    }
    if (detail) {
      if (
        this.dataSource.asset.asset.subType === FavoriteRequestType.CHANNEL_LOGO ||
        this.dataSource.asset.asset.subType === FavoriteRequestType.CHANNEL_TOGGLE
      ) {
        // nebula cache ribbons use channel-1 recipe
        this.imageUrl = fullUrlWithRecipe(this.dataSource.backend, detail.image, Recipes.Channel);
      } else {
        this.imageUrl = fullUrlWithRecipe(this.dataSource.backend, detail.image, this.dataSource.sizeInfo.recipe);
      }
    }
  }

  _updateFlags() {
    const props = this.dataSource.pageProperties;

    this._hideInfo = props.includes(CmsProperty.HideInfo);
    this._bestSchedule = props.includes(CmsProperty.UseBestSchedule);
    this._showSchedule = props.includes(CmsProperty.ShowScheduleInfo);
    this._showEpisode = props.includes(CmsProperty.ShowEpisodeInfo);
    this._adultRequired = props.includes(CmsProperty.AdultRequired);
    this._showProgress = props.includes(CmsProperty.ShowProgress);
    if (props.includes(CmsProperty.LiveThumbnail)) {
      this._showProgress = false;
    }
  }

  _setScheduleTheme() {
    this.topLineBold = true;
    // Use different colors for old EPG cards design
    if (this.useOldEpgCards) {
      this.topLineColor = Theme.Colors.Ribbon.Card.Text.Highlight;
      this.bottomLineColor = Theme.Colors.Ribbon.Card.Text.Normal;
    } else {
      this.topLineColor = Theme.Colors.Ribbon.Card.Text.Normal;
      this.bottomLineColor = Theme.Colors.Ribbon.Card.Text.Highlight;
    }
  }

  _setRecordingFolderAmount() {
    this.recordingFolderAmount = this.dataSource.nrOfRecordings;
  }

  getEpgIcons(): Promise<CardInfoIcon[]> {
    if (this._iconPromise) {
      return this._iconPromise;
    }
    this._iconPromise = (
      new Promise(resolve => {
        if (!this._showSchedule) {
          resolve([]);
          return;
        }
        if (
          this.extractor &&
          this.extractor.schedule &&
          this.extractor.schedule.programId &&
          this.extractor.channel?.channelId
        ) {
          resolve(
            this.dataSource
              .loadRecording(this.extractor.schedule.programId, this.extractor.channel.channelId)
              .then(() => {
                this._addAccessibilityIcons(this.extractor?.schedule?.accessibilityOptions);
                const recording = this.dataSource.recording;
                if (recording) {
                  this._addRecordingIcons(recording);
                }
                if (this.recordingFolderAmount <= 1) {
                  this._addPlayIcon(recording);
                }
                return this.icons;
              })
          );
        } else {
          resolve(this.icons);
        }
      }) as Promise<CardInfoIcon[]>
    ).catch(err => {
      Log.error('[CardInfo] error fetching icons', err);
      return [];
    });
    return this._iconPromise;
  }

  _addAccessibilityIcons(tags: string[] = []) {
    // comment next line to enable icons without global setting
    if (!this.dataSource.backend.globalSettings.boolValue(GlobalSettingsKeys.showAccessibilityIcons)) return;
    // manual if test checking to ensure correct order
    if (tags.includes(AccessibilityTypes.SUBTITLES)) {
      this.icons.push({
        type: CmsCardIcon,
        icon: Utils.asset(AccessibilityIcons[AccessibilityTypes.SUBTITLES]),
      });
    }
    if (tags.includes(AccessibilityTypes.AUDIO_DESCRIPTION)) {
      this.icons.push({
        type: CmsCardIcon,
        icon: Utils.asset(AccessibilityIcons[AccessibilityTypes.AUDIO_DESCRIPTION]),
      });
    }
    if (tags.includes(AccessibilityTypes.SIGN_LANGUAGE)) {
      this.icons.push({
        type: CmsCardIcon,
        icon: Utils.asset(AccessibilityIcons[AccessibilityTypes.SIGN_LANGUAGE]),
      });
    }
  }

  _addRecordingIcons(recording: Recording) {
    const recIcons = EpgUtil.getRecordingIcons(recording);
    recIcons.forEach(recIcon => {
      this.icons.push({
        type: CmsCardIcon,
        icon: recIcon.src,
      });
    });
    if (this.recordingFolderAmount > 1) {
      this.icons.push({
        type: CmsFolderIcon,
        amount: this.recordingFolderAmount,
      });
    }
  }

  _addPlayIcon(recording: Recording | undefined) {
    const backend = this.dataSource.backend;
    if (this.extractor?.schedule) {
      const play = EpgUtil.getPlayIcon(
        backend,
        this.extractor?.schedule,
        recording,
        this.extractor?.channel,
        undefined,
        false
      );
      if (play) {
        this.icons.push({
          type: CmsCardIcon,
          icon: Utils.asset(play),
        });
      }
    }
  }

  getFavoriteIcon() {
    // console.log('[CardInfo] get favorite icon', this);
    let favObj: FavoriteID;
    if (this.dataSource.asset.asset.type === AssetType.VOD) {
      favObj = {
        id: this.dataSource.asset.asset.id,
        type: AssetType.VOD,
      };
    } else if (this.dataSource.asset.asset.type === AssetType.RECORDING) {
      favObj = {
        id: this.dataSource.recording?.series ? this.dataSource.recording?.series : '',
        type: AssetType.SERIES,
      };
    } else if (this.dataSource.asset.asset.seriesId) {
      favObj = {
        id: this.dataSource.asset.asset.seriesId,
        type: AssetType.SERIES,
      };
    } else {
      favObj = {
        id: this.dataSource.asset.asset.id,
        type: AssetType.SERIES,
      };
    }

    const result = this.dataSource.backend.cachedFavoriteApi.isFavorite(this.dataSource.backend, favObj);
    return result
      .then(res => {
        if (res) {
          return Utils.asset('./images/favorites/ic_star_open.png');
        } else {
          return undefined;
        }
      })
      .catch(err => {
        Log.error('[CardInfo] error fetching favorite icon', err);
        return undefined;
      });
  }
}
